import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';
import { EBookletAttachmentName } from '@common/enums/attachments.enum';
import { EUsersRole } from '@common/enums/users-role.enum';
import { BitfStrapiModel } from '@common/libs/bitforce/core/models';
import { EBookletStatus } from '@web/enums';
import get from 'get-value';
import { BookletAttachments } from './booklet-attachments.model';
import { Payment } from './payment.model';
import { Role } from './role.model';
import { User } from './user.model';

export class Booklet extends BitfStrapiModel {
  id?: number;
  documentId?: string;
  sellerName: string;
  sellerSurname: string;
  sellerEmail?: string;
  sellerMobilePhone?: number;
  propertyAddress: string;
  sellerFiscalCode?: string;
  propertyDescription?: string;
  propertyLastWorkData?: Date; // TODO: fix the name Date not Data
  technicalReportSurveyor?: User;
  technicalReportSurveyorAcceptedAt?: Date;
  agent?: User;
  inviteSellerEmail?: string;
  bookletAttachments: BookletAttachments;
  secretaries?: User[];
  apeSurveyor?: User;
  apeSurveyorAcceptedAt?: Date;
  extraSurveyor?: User;
  extraSurveyorAcceptedAt?: Date;
  accountants?: User[];
  propertyDeedOfSale?: Date;
  bookletStartDate?: Date;
  bookletEndDate?: Date;
  apeStartDate?: Date;
  apeEndDate?: Date;
  technicalReportStartDate?: Date;
  technicalReportEndDate?: Date;
  technicalReportCostPaymentDate?: Date;
  extraStartDate?: Date;
  extraEndDate?: Date;
  technicalReportSurveyorAccessToRecordsDate?: Date;
  accounts?: User[];
  seller?: User;
  technicalReportSurveyorNote?: string;
  apeSurveyorNote?: string;
  extraSurveyorNote?: string;
  sellerAddress?: string;
  requestForDeedOfOrigin?: boolean;
  bookletNotes?: string;
  requestForApe?: boolean;
  requestForTechnicalReport?: boolean;
  acceptPaidServicesTerms?: boolean;
  acceptAgentDelegation?: boolean;
  publishedAt?: string;
  createdBy?: User;
  updatedBy?: User;
  locale?: string;
  localizations?: any[];
  extraContractHtml?: string;
  costSummaryHtml?: string;
  extraCostSummaryHtml?: string;
  unifiedCostSummaryHtml?: string;
  enableSharing?: boolean;
  casaDocCollectionDate?: Date;
  bookletStatus?: EBookletStatus;

  technicalReportGrossPrice?: number;
  technicalReportCost?: number;
  technicalReportCasaDocNetPrice?: number;
  technicalReportCasaDocVatCost?: number;
  technicalReportCasaDocGrossPrice?: number;

  requestForDeedOfOriginGrossPrice?: number;
  requestForDeedOfOriginNetPrice?: number;
  requestForDeedOfOriginVatCost?: number;

  requestForDeedOfOriginCostNetPrice?: number;
  requestForDeedOfOriginCostGrossPrice?: number;
  requestForDeedOfOriginCostVatCost?: number;

  apeGrossPrice?: number;
  apeCasaDocNetPrice?: number;
  apeCasaDocVatCost?: number;
  apeCasaDocGrossPrice?: number;

  extraSurveyorNetPrice?: number;
  extraCasaDocNetPrice?: number;
  extraCasaDocVatCost?: number;
  extraCasaDocGrossPrice?: number;
  extraAccountNetPrice?: number;
  extraAccountVatCost?: number;
  extraAccountGrossPrice?: number;
  extraCost?: number;
  extraGrossPrice?: number;
  extraCasaDocPercentage?: number;
  extraAccountPercentage?: number;
  extraAccount: User;
  extraBookletProformaInvoiceDate?: Date;
  extraCasaDocCollectionDate?: Date;
  extraBookletInvoiceSendDate?: Date;
  extraBookletFicInvoiceId?: string;
  extraBookletFicInvoiceNumber?: string;

  casaDocRevenues?: number;
  bookletGrossPrice?: number;
  secretariesFeesGrossPrice?: number;
  accountsFeesGrossPrice?: number;
  accountantsFeesGrossPrice?: number;
  prepaidCasaDocCost?: number;
  employeeCasaDocCost?: number;
  bookletCasaDocCost?: number;
  surveyorsTransferAmount?: number;
  businessProfit?: number;
  contributionMargin?: number;

  paymentsByRole: {
    id: number;
    ape: Payment;
    extra: Payment;
    technicalReport: Payment;
    accountants: Payment[];
    secretaries: Payment[];
    accounts: Payment[];
    extraAccount: Payment;
  };
  ownersName: string;
  /** Valorizzata dal backend quando ownersName passa da vuoto a valorizzato la prima volta */
  ownersNameCompletedAt?: Date;
  revenueStampTotal?: number;
  accessToRecordsProtocolNumber?: string;
  accessToRecordsRequestDate?: Date;
  partnerNote?: string;
  isSellerContacted?: boolean;
  casaDocContractSignatureDate?: Date;
  extraContractSignatureDate?: Date;
  bookletProformaInvoiceDate?: Date;
  bookletInvoiceSendDate?: Date;
  bookletFicInvoiceId?: string;
  bookletFicInvoiceNumber?: string;
  bookletAlert?: string;
  enableEmailNotification?: boolean;

  constructor(data: Partial<Booklet> = {}) {
    super({ data });
    if (data.bookletStartDate) {
      this.bookletStartDate = this.parseDate(data.bookletStartDate);
    }
    if (data.bookletEndDate) {
      this.bookletEndDate = this.parseDate(data.bookletEndDate);
    }
    if (data.apeStartDate) {
      this.apeStartDate = this.parseDate(data.apeStartDate);
    }
    if (data.apeEndDate) {
      this.apeEndDate = this.parseDate(data.apeEndDate);
    }
    if (data.technicalReportStartDate) {
      this.technicalReportStartDate = this.parseDate(data.technicalReportStartDate);
    }
    if (data.technicalReportEndDate) {
      this.technicalReportEndDate = this.parseDate(data.technicalReportEndDate);
    }
    if (data.technicalReportCostPaymentDate) {
      this.technicalReportCostPaymentDate = this.parseDate(data.technicalReportCostPaymentDate);
    }

    if (data.extraStartDate) {
      this.extraStartDate = this.parseDate(data.extraStartDate);
    }
    if (data.extraEndDate) {
      this.extraEndDate = this.parseDate(data.extraEndDate);
    }
    if (data.propertyLastWorkData) {
      this.propertyLastWorkData = this.parseDate(data.propertyLastWorkData);
    }
    if (data.technicalReportSurveyorAccessToRecordsDate) {
      this.technicalReportSurveyorAccessToRecordsDate = new Date(
        data.technicalReportSurveyorAccessToRecordsDate
      );
    }
    if (data.propertyDeedOfSale) {
      this.propertyDeedOfSale = this.parseDate(data.propertyDeedOfSale);
    }

    if (data.casaDocCollectionDate) {
      this.casaDocCollectionDate = this.parseDate(data.casaDocCollectionDate);
    }

    if (data.accessToRecordsRequestDate) {
      this.accessToRecordsRequestDate = this.parseDate(data.accessToRecordsRequestDate);
    }

    if (data.casaDocContractSignatureDate) {
      this.casaDocContractSignatureDate = this.parseDate(data.casaDocContractSignatureDate);
    }

    if (data.extraContractSignatureDate) {
      this.extraContractSignatureDate = this.parseDate(data.extraContractSignatureDate);
    }

    if (data.ownersNameCompletedAt) {
      this.ownersNameCompletedAt = this.parseDate(data.ownersNameCompletedAt);
    }

    if (data.bookletProformaInvoiceDate) {
      this.bookletProformaInvoiceDate = this.parseDate(data.bookletProformaInvoiceDate);
    }

    if (data.bookletInvoiceSendDate) {
      this.bookletInvoiceSendDate = this.parseDate(data.bookletInvoiceSendDate);
    }

    if (data.extraBookletProformaInvoiceDate) {
      this.extraBookletProformaInvoiceDate = this.parseDate(data.extraBookletProformaInvoiceDate);
    }

    if (data.extraCasaDocCollectionDate) {
      this.extraCasaDocCollectionDate = this.parseDate(data.extraCasaDocCollectionDate);
    }

    if (data.extraBookletInvoiceSendDate) {
      this.extraBookletInvoiceSendDate = this.parseDate(data.extraBookletInvoiceSendDate);
    }

    if (data.seller) {
      this.seller = new User(this.seller);
    }
    if (data.agent) {
      this.agent = new User(this.agent);
    }
    if (data.accounts) {
      this.accounts = data.accounts.map((accountData) => new User(accountData));
    }
    if (data.secretaries) {
      this.secretaries = data.secretaries.map((secretaryData) => new User(secretaryData));
    }
    if (data.technicalReportSurveyor) {
      this.technicalReportSurveyor = new User(this.technicalReportSurveyor);
    }

    if (data.technicalReportSurveyorAcceptedAt) {
      this.technicalReportSurveyorAcceptedAt = this.parseDate(data.technicalReportSurveyorAcceptedAt);
    }

    if (data.apeSurveyor) {
      this.apeSurveyor = new User(this.apeSurveyor);
    }

    if (data.apeSurveyorAcceptedAt) {
      this.apeSurveyorAcceptedAt = this.parseDate(data.apeSurveyorAcceptedAt);
    }

    if (data.extraSurveyor) {
      this.extraSurveyor = new User(this.extraSurveyor);
    }

    if (data.extraAccount) {
      this.extraAccount = new User(this.extraAccount);
    }

    if (data.extraSurveyorAcceptedAt) {
      this.extraSurveyorAcceptedAt = this.parseDate(data.extraSurveyorAcceptedAt);
    }

    if (data.accountants) {
      this.accountants = data.accountants.map((accountantData) => new User(accountantData));
    }

    if (data.paymentsByRole) {
      this.paymentsByRole = {
        id: data.paymentsByRole.id,
        ape: data.paymentsByRole.ape ? new Payment(data.paymentsByRole.ape) : null,
        extra: data.paymentsByRole.extra ? new Payment(data.paymentsByRole.extra) : null,
        technicalReport: data.paymentsByRole.technicalReport
          ? new Payment(data.paymentsByRole.technicalReport)
          : null,
        accountants: (data.paymentsByRole.accountants || []).map((paymentData) => new Payment(paymentData)),
        secretaries: (data.paymentsByRole.secretaries || []).map((paymentData) => new Payment(paymentData)),
        accounts: (data.paymentsByRole.accounts || []).map((paymentData) => new Payment(paymentData)),
        extraAccount: data.paymentsByRole.extraAccount ? new Payment(data.paymentsByRole.extraAccount) : null,
      };
    }

    this.bookletAttachments = new BookletAttachments(data.bookletAttachments);
  }

  get sellerFromBooklet(): User | null {
    return new User({
      id: this.seller?.id || 0,
      documentId: this.seller?.documentId || '',
      name: this.sellerName,
      surname: this.sellerSurname,
      email: this.sellerEmail,
      phoneNumber: this.sellerMobilePhone,
      role: new Role({
        type: EUsersRole.SELLER,
      }),
    });
  }

  get serialised() {
    return {
      id: this.id,
      documentId: this.documentId,
      sellerName: this.sellerName,
      sellerSurname: this.sellerSurname,
      sellerEmail: this.sellerEmail,
      sellerMobilePhone: this.sellerMobilePhone,
      propertyAddress: this.propertyAddress,
      sellerFiscalCode: this.sellerFiscalCode,
      propertyDescription: this.propertyDescription,
      propertyLastWorkData: this.propertyLastWorkData,
      technicalReportSurveyor: this.technicalReportSurveyor,
      agent: this.agent,
      inviteSellerEmail: this.inviteSellerEmail,
      bookletAttachments: this.bookletAttachments?.serialised,
      secretaries: this.secretaries,
      apeSurveyor: this.apeSurveyor,
      extraSurveyor: this.extraSurveyor,
      accountants: this.accountants,
      propertyDeedOfSale: this.propertyDeedOfSale,
      bookletStartDate: this.bookletStartDate,
      bookletEndDate: this.bookletEndDate,
      apeStartDate: this.apeStartDate,
      apeEndDate: this.apeEndDate,
      technicalReportStartDate: this.technicalReportStartDate,
      technicalReportEndDate: this.technicalReportEndDate,
      technicalReportCostPaymentDate: this.technicalReportCostPaymentDate,
      extraStartDate: this.extraStartDate,
      extraEndDate: this.extraEndDate,
      technicalReportSurveyorAccessToRecordsDate: this.technicalReportSurveyorAccessToRecordsDate,
      accounts: this.accounts,
      seller: this.seller,
      technicalReportSurveyorNote: this.technicalReportSurveyorNote,
      apeSurveyorNote: this.apeSurveyorNote,
      extraSurveyorNote: this.extraSurveyorNote,
      sellerAddress: this.sellerAddress,
      requestForDeedOfOrigin: this.requestForDeedOfOrigin,
      bookletNotes: this.bookletNotes,
      technicalReportGrossPrice: this.technicalReportGrossPrice,
      technicalReportCost: this.technicalReportCost,
      requestForDeedOfOriginGrossPrice: this.requestForDeedOfOriginGrossPrice,
      extraSurveyorNetPrice: this.extraSurveyorNetPrice,
      extraCost: this.extraCost,
      requestForApe: this.requestForApe,
      requestForTechnicalReport: this.requestForTechnicalReport,
      acceptPaidServicesTerms: this.acceptPaidServicesTerms,
      acceptAgentDelegation: this.acceptAgentDelegation,
      apeGrossPrice: this.apeGrossPrice,
      publishedAt: this.publishedAt,
      createdBy: this.createdBy,
      updatedBy: this.updatedBy,
      locale: this.locale,
      localizations: this.localizations,
      extraContractHtml: this.extraContractHtml,
      costSummaryHtml: this.costSummaryHtml,
      extraCostSummaryHtml: this.extraCostSummaryHtml,
      unifiedCostSummaryHtml: this.unifiedCostSummaryHtml,
      casaDocCollectionDate: this.casaDocCollectionDate,
      bookletStatus: this.bookletStatus,
      technicalReportCasaDocNetPrice: this.technicalReportCasaDocNetPrice,
      technicalReportCasaDocVatCost: this.technicalReportCasaDocVatCost,
      technicalReportCasaDocGrossPrice: this.technicalReportCasaDocGrossPrice,
      requestForDeedOfOriginNetPrice: this.requestForDeedOfOriginNetPrice,
      requestForDeedOfOriginCostGrossPrice: this.requestForDeedOfOriginCostGrossPrice,
      requestForDeedOfOriginCostNetPrice: this.requestForDeedOfOriginCostNetPrice,
      requestForDeedOfOriginCostVatCost: this.requestForDeedOfOriginCostVatCost,
      requestForDeedOfOriginVatCost: this.requestForDeedOfOriginVatCost,
      apeCasaDocNetPrice: this.apeCasaDocNetPrice,
      apeCasaDocVatCost: this.apeCasaDocVatCost,
      apeCasaDocGrossPrice: this.apeCasaDocGrossPrice,
      extraCasaDocNetPrice: this.extraCasaDocNetPrice,
      extraCasaDocVatCost: this.extraCasaDocVatCost,
      extraCasaDocGrossPrice: this.extraCasaDocGrossPrice,
      extraAccountNetPrice: this.extraAccountNetPrice,
      extraAccountVatCost: this.extraAccountVatCost,
      extraAccountGrossPrice: this.extraAccountGrossPrice,
      extraGrossPrice: this.extraGrossPrice,
      extraCasaDocPercentage: this.extraCasaDocPercentage,
      extraAccountPercentage: this.extraAccountPercentage,
      extraBookletProformaInvoiceDate: this.extraBookletProformaInvoiceDate,
      extraCasaDocCollectionDate: this.extraCasaDocCollectionDate,
      extraBookletInvoiceSendDate: this.extraBookletInvoiceSendDate,
      extraBookletFicInvoiceId: this.extraBookletFicInvoiceId,
      extraBookletFicInvoiceNumber: this.extraBookletFicInvoiceNumber,
      casaDocRevenues: this.casaDocRevenues,
      bookletGrossPrice: this.bookletGrossPrice,
      secretariesFeesGrossPrice: this.secretariesFeesGrossPrice,
      accountsFeesGrossPrice: this.accountsFeesGrossPrice,
      accountantsFeesGrossPrice: this.accountantsFeesGrossPrice,
      prepaidCasaDocCost: this.prepaidCasaDocCost,
      employeeCasaDocCost: this.employeeCasaDocCost,
      bookletCasaDocCost: this.bookletCasaDocCost,
      surveyorsTransferAmount: this.surveyorsTransferAmount,
      businessProfit: this.businessProfit,
      contributionMargin: this.contributionMargin,
      ownersName: this.ownersName,
      revenueStampTotal: this.revenueStampTotal,
      accessToRecordsProtocolNumber: this.accessToRecordsProtocolNumber,
      accessToRecordsRequestDate: this.accessToRecordsRequestDate,
      ...(this.paymentsByRole && {
        paymentsByRole: {
          id: this.paymentsByRole.id,
          ape: this.paymentsByRole?.ape?.serialised,
          extra: this.paymentsByRole?.extra?.serialised,
          extraAccount: this.paymentsByRole?.extraAccount?.serialised,
          technicalReport: this.paymentsByRole?.technicalReport?.serialised,
          accountants: this.paymentsByRole?.accountants?.map((payment) => payment.serialised),
          accounts: this.paymentsByRole?.accounts?.map((payment) => payment.serialised),
          secretaries: this.paymentsByRole?.secretaries?.map((payment) => payment.serialised),
        },
      }),
      partnerNote: this.partnerNote,
      isSellerContacted: this.isSellerContacted,
      bookletProformaInvoiceDate: this.bookletProformaInvoiceDate,
      bookletInvoiceSendDate: this.bookletInvoiceSendDate,
      bookletFicInvoiceId: this.bookletFicInvoiceId,
      bookletFicInvoiceNumber: this.bookletFicInvoiceNumber,
      bookletAlert: this.bookletAlert,
    };
  }

  get state() {
    const state = {
      dashboard: {
        warnings: [],
        showInformationWarningIcon: false,
        showSurveyorsWarningIcon: false,
        showContractsWarningIcon: false,
        showDocumentsWarningIcon: false,
      },
      information: {
        warnings: [],
      },
      surveyors: {
        warnings: [],
      },
      contracts: {
        warnings: [],
        canSignCasaDocContract: false,
        canSignExtraContract: false,
      },
      documents: {
        warnings: [],
      },
      apeAndDeedOfOrigin: {
        warnings: [],
      },
    };

    const hasInformationWarnings = this.hasInformationWarnings();

    if (hasInformationWarnings) {
      state.dashboard.showInformationWarningIcon = true;
      state.information.warnings = [bitfToTranslate('COMMON.WARNINGS.MISSING_REQUIRED_DATA')];
    } else {
      if (
        this.bookletAttachments?.sellerIdentityCard?.file &&
        !this.bookletAttachments?.sellerIdentityCard?.compliant
      ) {
        state.dashboard.showInformationWarningIcon = true;
        state.information.warnings = [bitfToTranslate('COMMON.WARNINGS.IDENTITY_CARD_UNDER_CHECK')];
      }
    }

    //contracts

    if (hasInformationWarnings) {
      //controllo che ci siano tutti i dati necessari per i contratti
      state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.MISSING_REQUIRED_DATA_FOR_CONTRACTS')];
      state.dashboard.showContractsWarningIcon = true;
    } else if (
      //controllo che il documento sia stato caricato e verificato
      this.bookletAttachments?.sellerIdentityCard?.file &&
      !this.bookletAttachments?.sellerIdentityCard?.compliant
    ) {
      state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.IDENTITY_CARD_UNDER_CHECK')];
      state.dashboard.showContractsWarningIcon = true;
    } else {
      if (!this.technicalReportGrossPrice) {
        //controllo che il prezzo della relazione tecnica sia stato inserito
        state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.WAITING_FOR_TECHNICAL_REPORT_PRICE')];
        state.dashboard.showContractsWarningIcon = true;
      } else {
        if (this.requestForTechnicalReport) {
          // controllo che il contratto sia stato firmato solo se ha richiesto la relazione tecnica
          if (!this.bookletAttachments?.[EBookletAttachmentName.CASA_DOC_CONTRACT]?.file) {
            state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.MUST_SIGN_CASADOC_CONTRACTS')];
            state.contracts.canSignCasaDocContract = true;
            state.dashboard.showContractsWarningIcon = true;
          } else {
            //controllo che il contratto sia compliant
            if (!this.bookletAttachments?.[EBookletAttachmentName.CASA_DOC_CONTRACT]?.signed) {
              state.contracts.warnings = [
                bitfToTranslate('COMMON.WARNINGS.CASA_DOC_CONTRACT_ON_VERIFICATION'),
              ];
              state.dashboard.showContractsWarningIcon = true;
            }
          }
        }
      }
    }

    if (
      this.extraContractHtml &&
      this.bookletAttachments?.[EBookletAttachmentName.CASA_DOC_CONTRACT]?.signed &&
      !this.bookletAttachments?.[EBookletAttachmentName.EXTRA_CONTRACT]?.signed
    ) {
      if (!this.bookletAttachments?.[EBookletAttachmentName.EXTRA_CONTRACT]?.file) {
        state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.MUST_SIGN_EXTRA_CONTRACTS')];
        state.contracts.canSignExtraContract = true;
      } else if (!this.bookletAttachments?.[EBookletAttachmentName.EXTRA_CONTRACT]?.signed) {
        state.contracts.warnings = [bitfToTranslate('COMMON.WARNINGS.EXTRA_CONTRACT_ON_VERIFICATION')];
      }
      state.dashboard.showContractsWarningIcon = true;
    }

    //documents not Compliant
    if (this.hasDocumentsNotCompliant()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.documents.warnings = [
        bitfToTranslate('COMMON.WARNINGS.DOCUMENTS_NOT_COMPLIANT_OR_UNDER_VERIFICATION'),
      ];
    }
    //Documents Unpaid
    if (this.hasDocumentsUnpaid()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.documents.warnings = [bitfToTranslate('COMMON.WARNINGS.DOCUMENTS_NOT_PAID')];
    }
    //Ape and Deed of Origin
    if (this.hasApeNotLoaded()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.apeAndDeedOfOrigin.warnings.push(bitfToTranslate('COMMON.WARNINGS.APE_NOT_LOADED'));
    }
    if (this.hasDeedOfOriginNotLoaded()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.apeAndDeedOfOrigin.warnings.push(bitfToTranslate('COMMON.WARNINGS.DEED_OF_ORIGIN_NOT_LOADED'));
    }

    if (this.hasApeSurveyorPicturesNotLoaded()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.apeAndDeedOfOrigin.warnings.push(
        bitfToTranslate('COMMON.WARNINGS.APE_SURVEYOR_PICTURE_NOT_LOADED')
      );
    }

    if (this.hasApeSurveyorSurveyNotLoaded()) {
      state.dashboard.showDocumentsWarningIcon = true;
      state.apeAndDeedOfOrigin.warnings.push(
        bitfToTranslate('COMMON.WARNINGS.APE_SURVEYOR_SURVEY_NOT_LOADED')
      );
    }
    return state;
  }

  get isBookletNotComplete() {
    return (
      this.state.dashboard.showInformationWarningIcon ||
      this.state.dashboard.showSurveyorsWarningIcon ||
      this.state.dashboard.showContractsWarningIcon ||
      this.state.dashboard.showDocumentsWarningIcon
    );
  }

  get hasCasaDocContractSigned() {
    return this.bookletAttachments?.[EBookletAttachmentName.CASA_DOC_CONTRACT]?.signed;
  }

  hasInformationWarnings() {
    const requiredAttributes = [
      'sellerName',
      'sellerSurname',
      'sellerEmail',
      'sellerFiscalCode',
      'sellerMobilePhone',
      'propertyAddress',
      'bookletAttachments.sellerIdentityCard.file',
    ];

    const hasInformationWarnings = requiredAttributes.some((attr) => !get(this.serialised, attr));

    return hasInformationWarnings;
  }

  hasApeNotLoaded() {
    if (this.hasCasaDocContractSigned) {
      return !this.requestForApe && !this.bookletAttachments.ape?.file;
    }
    return false;
  }

  hasApeSurveyorPicturesNotLoaded() {
    return this.requestForApe && !this.bookletAttachments.apeSurveyorPictures?.file;
  }

  hasApeSurveyorSurveyNotLoaded() {
    return this.requestForApe && !this.bookletAttachments.apeSurveyorSurvey?.file;
  }

  hasDeedOfOriginNotLoaded() {
    if (this.hasCasaDocContractSigned) {
      return !this.requestForDeedOfOrigin && !this.bookletAttachments.deedOfOrigin?.file;
    }
    return false;
  }

  hasDocumentsNotCompliant() {
    let hasDocumentsNotCompliant = false;
    // Questi documenti generici vendono controllati e dato il warning se non sono compliant
    if (this.bookletAttachments?.landRegistry?.file && !this.bookletAttachments?.landRegistry?.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.urbanPlanning?.file && !this.bookletAttachments?.urbanPlanning?.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (
      this.bookletAttachments?.occupancyPermit?.file &&
      !this.bookletAttachments?.occupancyPermit?.compliant
    ) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.systems?.file && !this.bookletAttachments?.systems?.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.boiler?.file && !this.bookletAttachments?.boiler?.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.cdu?.file && !this.bookletAttachments?.cdu?.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (
      this.bookletAttachments?.latentDefectsInsurance?.file &&
      !this.bookletAttachments?.latentDefectsInsurance?.compliant
    ) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.deedOfOrigin?.file && !this.bookletAttachments.deedOfOrigin.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (this.bookletAttachments?.ape?.file && !this.bookletAttachments.ape.compliant) {
      hasDocumentsNotCompliant = true;
    }
    if (
      this.bookletAttachments?.apeSurveyorSurvey?.file &&
      !this.bookletAttachments.apeSurveyorSurvey.compliant
    ) {
      hasDocumentsNotCompliant = true;
    }
    if (
      this.bookletAttachments?.apeSurveyorPictures?.file &&
      !this.bookletAttachments.apeSurveyorPictures.compliant
    ) {
      hasDocumentsNotCompliant = true;
    }

    if (
      this.bookletAttachments?.technicalReport?.file &&
      !this.bookletAttachments.technicalReport.compliant
    ) {
      hasDocumentsNotCompliant = true;
    }

    if (this.bookletAttachments?.extra?.file && !this.bookletAttachments.extra.compliant) {
      hasDocumentsNotCompliant = true;
    }

    return hasDocumentsNotCompliant;
  }

  hasDocumentsUnpaid() {
    let hasDocumentsUnpaid = false;

    if (
      this.requestForApe &&
      this.bookletAttachments?.ape?.file &&
      this.bookletAttachments.ape.compliant &&
      !this.bookletAttachments?.ape?.paid
    ) {
      hasDocumentsUnpaid = true;
    }

    if (
      this.bookletAttachments?.technicalReport?.file &&
      this.bookletAttachments.technicalReport.compliant &&
      !this.bookletAttachments?.technicalReport?.paid
    ) {
      hasDocumentsUnpaid = true;
    }

    if (
      this.bookletAttachments?.extra?.file &&
      this.bookletAttachments.extra.compliant &&
      !this.bookletAttachments?.extra?.paid
    ) {
      hasDocumentsUnpaid = true;
    }

    if (
      this.requestForDeedOfOrigin &&
      this.bookletAttachments?.deedOfOrigin?.file &&
      this.bookletAttachments.deedOfOrigin.compliant &&
      !this.bookletAttachments?.deedOfOrigin?.paid
    ) {
      hasDocumentsUnpaid = true;
    }

    return hasDocumentsUnpaid;
  }

  // parse a date in format YYYY-MM-DD to be italian local date
  private parseDate(dateString: string | Date): Date | null {
    if (dateString instanceof Date) {
      return dateString;
    }
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}
